import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import "./App.css";
import { AppProvider } from "./context/AppContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import NewsletterPopup from "./components/NewsletterPopup";
import Home from "./pages/Home";
import CataloguePage from "./pages/CataloguePage";
import CategoryPage from "./pages/CategoryPage";
import ProductDetail from "./pages/ProductDetail";
import Cart from "./pages/Cart";
import AnalyticsScripts from "./components/AnalyticsScripts";
import CookieBanner from "./components/CookieBanner";
import PromoBanner from "./components/PromoBanner";
import PhysicalSalesBanner, { SalesStatusProvider } from "./components/PhysicalSalesBanner";
import UpsellPackModal from "./components/UpsellPackModal";
import { useApp } from "./context/AppContext";
import WelcomeBanner from "./components/WelcomeBanner";
import ChatAssistant from "./components/ChatAssistant";
import { initAffiliateTracking } from "./lib/affiliateTracker";

// --- Lazy-loaded routes (below-the-fold pages, admin, legal, etc.) ---
const Success = lazy(() => import("./pages/Success"));
const About = lazy(() => import("./pages/About"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const MentionsLegales = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.MentionsLegales })));
const CGV = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.CGV })));
const RGPD = lazy(() => import("./pages/LegalPages").then(m => ({ default: m.RGPD })));
const BlogPage = lazy(() => import("./pages/BlogPages").then(m => ({ default: m.BlogPage })));
const ArticleDetailPage = lazy(() => import("./pages/BlogPages").then(m => ({ default: m.ArticleDetailPage })));
const EspacePro = lazy(() => import("./pages/EspacePro"));
const Fidelite = lazy(() => import("./pages/Fidelite"));
const MesCommandes = lazy(() => import("./pages/MesCommandes"));
import OrderTracking from "./pages/OrderTracking";
const Affiliation = lazy(() => import("./pages/Affiliation"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const Bundles = lazy(() => import("./pages/Bundles"));
const Quiz = lazy(() => import("./pages/Quiz"));
const TroublesPage = lazy(() => import("./pages/TroublesPage"));
const ClaimGiftPage = lazy(() => import("./pages/ClaimGiftPage"));
const DownloadableResources = lazy(() => import("./pages/DownloadableResources"));
const ResourceDetail = lazy(() => import("./pages/ResourceDetail"));
const LogicielsPage = lazy(() => import("./pages/LogicielsPage"));
const SoftwareDetailPage = lazy(() => import("./pages/SoftwareDetailPage"));
const ProposerLogicielPage = lazy(() => import("./pages/ProposerLogicielPage"));
const ProposerLogicielPayePage = lazy(() => import("./pages/ProposerLogicielPayePage"));
const GameLauncherPage = lazy(() => import("./pages/GameLauncherPage"));
const ParBesoinPage = lazy(() => import("./pages/ParBesoinPage"));
const MesRessources = lazy(() => import("./pages/MesRessources"));
const UnsubscribePage = lazy(() => import("./pages/UnsubscribePage"));
const FlyerFlashReadPage = lazy(() => import("./pages/FlyerFlashReadPage"));
const UnifiedSearch = lazy(() => import("./pages/UnifiedSearch"));

// === Modules Géni (Phase Turbo) ===
const OutilsHub = lazy(() => import("./pages/outils/OutilsHub"));
const GrilleMotsPage = lazy(() => import("./pages/outils/GrilleMotsPage"));
const TableauDoubleEntreePage = lazy(() => import("./pages/outils/TableauDoubleEntreePage"));
const ImagierListPage = lazy(() => import("./pages/outils/ImagierPages").then(m => ({ default: m.ImagierListPage })));
const ImagierDetailPage = lazy(() => import("./pages/outils/ImagierPages").then(m => ({ default: m.ImagierDetailPage })));
const LireImagePage = lazy(() => import("./pages/outils/LireImagePage"));
const CliniciansArticlesList = lazy(() => import("./pages/ArticlesPages").then(m => ({ default: m.ArticlesListPage })));
const CliniciansArticleDetail = lazy(() => import("./pages/ArticlesPages").then(m => ({ default: m.ArticleDetailPage })));

// Petit composant pour rediriger /blog/:slug → /articles/:slug
function LegacyBlogRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/articles/${slug}`} replace />;
}

// Track UTM source on every page load (no-op if no UTM param)
if (typeof window !== "undefined") {
  initAffiliateTracking();
}

// ── SEO — URL canonique côté client (Google Search Console: éviter les doublons) ──
// Si l'utilisateur (ou un bot) arrive sur /catalogue/ (slash final) ou /Catalogue,
// on remplace l'URL par la version canonique /catalogue SANS full reload (replaceState).
// Le canonical <link> émis par <Seo/> pointe déjà vers la version normalisée : cette étape
// est un renfort utile pour l'UX (l'utilisateur voit l'URL propre) et pour les moteurs
// qui exécutent JS.
if (typeof window !== "undefined") {
  try {
    const { pathname, search, hash } = window.location;
    let canonical = pathname;
    // Retire les slashes finaux (sauf racine)
    if (canonical.length > 1 && canonical.endsWith("/")) {
      canonical = canonical.replace(/\/+$/, "");
    }
    // Force lowercase du chemin (les slugs sont déjà lowercase — protection anti-doublon)
    const lower = canonical.toLowerCase();
    if (lower !== canonical) canonical = lower;
    if (canonical !== pathname) {
      window.history.replaceState({}, "", canonical + search + hash);
    }
  } catch { /* silent */ }
}

// Reusable input class injected
const style = document.createElement("style");
style.innerHTML = `.input { width:100%; background:#F1EFEB; border-radius:9999px; padding:.7rem 1.1rem; outline:none; font-size:.875rem; } .input:focus { box-shadow:0 0 0 2px rgba(26,54,93,.25); } textarea.input { border-radius:1.2rem; }`;
if (typeof document !== "undefined" && !document.getElementById("input-style")) {
  style.id = "input-style";
  document.head.appendChild(style);
}

function RouteFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="route-loading">
      <div className="w-8 h-8 border-2 border-pedablue border-t-transparent rounded-full animate-spin" aria-label="Chargement" />
    </div>
  );
}

function Shell({ children }) {
  const { upsellSlug, closeUpsell } = useApp() || {};
  return (
    <>
      <PromoBanner />
      <PhysicalSalesBanner dismissable={true} />
      <WelcomeBanner />
      <Navbar />
      <main className="min-h-[60vh]">{children}</main>
      <Footer />
      <NewsletterPopup />
      <ChatAssistant />
      <UpsellPackModal triggerSlug={upsellSlug} onClose={closeUpsell} />
    </>
  );
}

function BareShell({ children }) {
  return <main className="min-h-screen">{children}</main>;
}

function App() {
  return (
    <AppProvider>
      <SalesStatusProvider>
        <BrowserRouter>
          <AnalyticsScripts />
          <CookieBanner />
          <Toaster position="top-center" richColors />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Shell><Home /></Shell>} />
            <Route path="/catalogue" element={<Shell><CataloguePage /></Shell>} />
            <Route path="/categorie/:slug" element={<Shell><CategoryPage /></Shell>} />
            <Route path="/produit/:slug" element={<Shell><ProductDetail /></Shell>} />
            <Route path="/cart" element={<Shell><Cart /></Shell>} />
            <Route path="/panier" element={<Shell><Cart /></Shell>} />
            <Route path="/success" element={<Shell><Success /></Shell>} />
            <Route path="/commande/:orderId/suivi" element={<Shell><OrderTracking /></Shell>} />
            <Route path="/notre-histoire" element={<Shell><About /></Shell>} />
            <Route path="/about" element={<Shell><About /></Shell>} />
            <Route path="/mentions-legales" element={<Shell><MentionsLegales /></Shell>} />
            <Route path="/desabonner" element={<Shell><UnsubscribePage /></Shell>} />
            {/* Modules Orthophonie Online — outils cliniciens gratuits + bibliothèque */}
            <Route path="/outils" element={<Shell><OutilsHub /></Shell>} />
            <Route path="/outils/grille-mots" element={<Shell><GrilleMotsPage /></Shell>} />
            <Route path="/outils/tableau-double-entree" element={<Shell><TableauDoubleEntreePage /></Shell>} />
            <Route path="/outils/imagier" element={<Shell><ImagierListPage /></Shell>} />
            <Route path="/outils/imagier/:slug" element={<Shell><ImagierDetailPage /></Shell>} />
            <Route path="/articles" element={<Shell><CliniciansArticlesList /></Shell>} />
            <Route path="/articles/:slug" element={<Shell><CliniciansArticleDetail /></Shell>} />
            {/* Redirections legacy blog → articles */}
            <Route path="/blog" element={<Navigate to="/articles" replace />} />
            <Route path="/blog/:slug" element={<LegacyBlogRedirect />} />
            <Route path="/flyer-flashread" element={<FlyerFlashReadPage />} />
            <Route path="/cgv" element={<Shell><CGV /></Shell>} />
            <Route path="/rgpd" element={<Shell><RGPD /></Shell>} />
            <Route path="/blog" element={<Shell><BlogPage /></Shell>} />
            <Route path="/blog/:slug" element={<Shell><ArticleDetailPage /></Shell>} />
            <Route path="/espace-pro" element={<Shell><EspacePro /></Shell>} />
            <Route path="/fidelite" element={<Shell><Fidelite /></Shell>} />
            <Route path="/programme-fidelite" element={<Shell><Fidelite /></Shell>} />
            <Route path="/mes-commandes" element={<Shell><MesCommandes /></Shell>} />
            <Route path="/suivi-commande" element={<Shell><MesCommandes /></Shell>} />
            <Route path="/affiliation" element={<Shell><Affiliation /></Shell>} />
            <Route path="/affilies" element={<Shell><Affiliation /></Shell>} />
            <Route path="/favoris" element={<Shell><Wishlist /></Shell>} />
            <Route path="/wishlist" element={<Shell><Wishlist /></Shell>} />
            <Route path="/packs" element={<Shell><Bundles /></Shell>} />
            <Route path="/bundles" element={<Shell><Bundles /></Shell>} />
            <Route path="/quiz" element={<Shell><Quiz /></Shell>} />
            <Route path="/troubles/:slug" element={<Shell><TroublesPage /></Shell>} />
            <Route path="/reclamer-cadeau" element={<Shell><ClaimGiftPage /></Shell>} />
            <Route path="/ressources-a-telecharger" element={<Shell><DownloadableResources /></Shell>} />
            <Route path="/ressources-a-telecharger/:id" element={<Shell><ResourceDetail /></Shell>} />
            <Route path="/logiciels" element={<Shell><LogicielsPage /></Shell>} />
            <Route path="/logiciels/proposer" element={<Shell><ProposerLogicielPage /></Shell>} />
            <Route path="/logiciels/proposer/paye" element={<Shell><ProposerLogicielPayePage /></Shell>} />
            <Route path="/logiciels/:slug" element={<Shell><SoftwareDetailPage /></Shell>} />
            <Route path="/jouer-flashread" element={<GameLauncherPage gameSlug="flashread" />} />
            <Route path="/par-besoin" element={<ParBesoinPage />} />
            <Route path="/mes-ressources" element={<Shell><MesRessources /></Shell>} />
            <Route path="/recherche" element={<Shell><UnifiedSearch /></Shell>} />
            <Route path="/admin/login" element={<BareShell><AdminLogin /></BareShell>} />
            <Route path="/admin" element={<BareShell><AdminDashboard /></BareShell>} />
          </Routes>
        </Suspense>
        </BrowserRouter>
      </SalesStatusProvider>
    </AppProvider>
  );
}

export default App;
